import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faUsers } from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import _ from 'lodash';
import { formatBillingDate } from 'util/organizationBillingFormat';
import { useNavigate } from 'react-router-dom';
import SimpleBarReact from 'simplebar-react';

import { useListRepositoryUsersQuery } from 'rtk/org-admin/notaries.slice';
import NotaryViewModal from './NotaryViewModal';

const DEFAULT_PAGE_SIZE = 10;

function getJournalAccessBadgeVariant(journalAccess) {
  const value = String(journalAccess || '').toLowerCase();
  if (value === 'n/a' || value === 'none') return 'danger';
  if (value === 'limited') return 'warning';
  return 'success';
}

function getRepositoryUserGuid(user) {
  return (
    user?.UserGuid ||
    user?.UserGUID ||
    user?.UserMasterGuid ||
    user?.UserMasterGUID
  );
}

function formatLocations(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return '—';
  const labels = locations
    .map(loc => {
      if (typeof loc === 'string') return loc.trim();
      return (loc?.DisplayName || loc?.Name || loc?.LocationName || '').trim();
    })
    .filter(Boolean);
  return labels.length ? labels.join(', ') : '—';
}

const AdminNotariesUsers = () => {
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewUser, setViewUser] = useState(null);

  const indexStart = pageIndex * pageSize;

  const {
    data: usersRes,
    isFetching,
    isLoading,
    isError: usersLoadError,
    refetch
  } = useListRepositoryUsersQuery({
    indexStart,
    maxResults: pageSize,
    search: searchTerm
  });

  const items = usersLoadError ? [] : usersRes?.Objects || [];
  const totalRecords = usersRes?.TotalRecords || 0;
  const endOfResults = !!usersRes?.EndOfResults;

  const loading = isFetching || isLoading;

  const debouncedSetSearch = useMemo(() => {
    return _.debounce(value => {
      setSearchTerm(value);
      setPageIndex(0);
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const onSearchChange = e => {
    const value = e.target.value || '';
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const handleViewJournals = user => {
    const guid = getRepositoryUserGuid(user);
    if (!guid) return;
    navigate(`/admin/notaries/${guid}/entries`);
  };

  const openNotaryView = user => {
    const guid = getRepositoryUserGuid(user);
    if (!guid) return;
    setViewUser({
      guid,
      name: user?.FullName || user?.Email || ''
    });
  };

  const closeNotaryView = () => setViewUser(null);

  return (
    <Row className="mb-3 g-3">
      <Col lg={12}>
        <FalconComponentCard noGuttersBottom>
          <FalconComponentCard.Header
            image={corner6}
            className="px-4"
            title="Notaries"
            subtitle="View associated notary accounts"
            endEl={
              <div className="mx-1">
                <InputGroup>
                  <Form.Control
                    type="search"
                    value={searchInput}
                    onChange={onSearchChange}
                    placeholder="Search notaries..."
                    name="search"
                  />
                  <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon="search" />
                  </InputGroup.Text>
                </InputGroup>
              </div>
            }
          />

          <FalconComponentCard.Body noLight className="py-0 px-4">
            {isLoading && (
              <Flex justifyContent="center" className="py-3">
                <Spinner animation="border" size="sm" />
              </Flex>
            )}

            {items.length > 0 && !isLoading && (
              <Pagination
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalRecords={totalRecords}
                currentCount={items.length}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                endOfResults={endOfResults}
                disabled={loading}
                pageSizeOptions={[5, 10, 15, 25]}
                onRefresh={() => refetch()}
                loading={loading}
              />
            )}

            {items.length > 0 && !usersLoadError && !isLoading && (
              <SimpleBarReact>
                <Table
                  striped
                  bordered
                  className={`fs--1 mb-0${
                    loading ? ' refreshing-skeleton' : ''
                  }`}
                >
                  <thead className="bg-200 text-900 text-nowrap align-middle">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Locations</th>
                      <th scope="col">Journal access</th>
                      <th scope="col">Data retention</th>
                      <th scope="col">Created</th>
                      <th className="text-end" scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(user => {
                      const guid = getRepositoryUserGuid(user);
                      const createdDate = formatBillingDate(
                        user?.CreatedAt,
                        'l, LT'
                      );
                      const journalAccess = user?.JournalAccess || '—';
                      const deletionEffectiveUtc =
                        user?.HostedDataDeletionEffectiveUtc;

                      return (
                        <tr
                          key={guid || user?.Email || createdDate}
                          className="align-middle white-space-nowrap"
                        >
                          <td className="align-middle py-2">
                            <div className="fw-semi-bold">
                              {user?.FullName || '—'}
                            </div>
                          </td>
                          <td className="align-middle py-2">
                            {user?.Email || '—'}
                          </td>
                          <td className="align-middle py-2">
                            {formatLocations(user?.Locations)}
                          </td>
                          <td className="align-middle py-2">
                            <SoftBadge
                              bg={getJournalAccessBadgeVariant(journalAccess)}
                              pill
                            >
                              {journalAccess}
                            </SoftBadge>
                          </td>
                          <td className="align-middle py-2">
                            {user?.HostedDataState === 'DeletionPending' ? (
                              <div>
                                <SoftBadge bg="warning" pill>
                                  Deletion pending
                                </SoftBadge>
                                <div className="text-600 fs--2 mt-1">
                                  Access ends{' '}
                                  {formatBillingDate(
                                    deletionEffectiveUtc,
                                    'l, LT'
                                  )}
                                </div>
                              </div>
                            ) : (
                              <SoftBadge bg="success" pill>
                                Active
                              </SoftBadge>
                            )}
                          </td>
                          <td className="align-middle py-2">{createdDate}</td>
                          <td className="align-middle py-2 text-end">
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="outline-dark"
                                size="sm"
                                title="View"
                                disabled={!guid}
                                onClick={() => openNotaryView(user)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={!guid}
                                onClick={() => handleViewJournals(user)}
                              >
                                View journals
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </SimpleBarReact>
            )}

            {items.length === 0 &&
              !loading &&
              (usersLoadError ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faUsers} size="3x" />
                  <p className="my-3 text-600 fs--1">
                    Unable to load notaries. Try again.
                  </p>
                </div>
              ) : (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faUsers} size="3x" />
                  <p className="my-3 text-600 fs--1">
                    No notaries found.
                    {searchTerm
                      ? ' Try adjusting your search.'
                      : ' Associated notaries will appear here.'}
                  </p>
                </div>
              ))}
          </FalconComponentCard.Body>
        </FalconComponentCard>
      </Col>

      <NotaryViewModal
        show={!!viewUser?.guid}
        onHide={closeNotaryView}
        notaryUserGuid={viewUser?.guid}
        notaryName={viewUser?.name}
      />
    </Row>
  );
};

export default AdminNotariesUsers;
